class MobileHeaderScroll {
    constructor() {
        this.header = document.querySelector('.section-header');
        this.headerWrapper = document.querySelector('.header-wrapper');

        if (!this.header || !this.headerWrapper) return;

        // Only run on mobile
        if (window.innerWidth >= 990) return;

        this.lastScrollTop = 0;
        this.scrollThreshold = 3; // Minimum scroll distance to trigger hide/show
        this.scrollUpThreshold = 3; // Amount user needs to scroll up to show header
        this.heroBanner = null;
        this.heroBannerHeight = 0;
        this.heroHeightTimeout = null;
        this.headerHeight = this.header.offsetHeight || 65;
        this.initialHideLockBuffer = 24; // Prevent hide near top by one header height + buffer
        this.initialHideLockDistance = this.headerHeight + this.initialHideLockBuffer;
        this.headerTranslateY = 0;
        this.isHeaderHidden = false;
        this.maxScrollUpPosition = 0; // Track the highest scroll position reached
        this.showAnimationDuration = 300; // Keep in sync with CSS transition
        this.hideAnimationDuration = 300;
        this.hideAfterShowDelay = 150; // Buffer before allowing hide again
        this.hideLockUntil = 0;
        this.showLockUntil = 0;
        this.isShowAnimationInProgress = false;
        this.isHideAnimationInProgress = false;
        this.showAnimationTimeout = null;
        this.hideAnimationTimeout = null;
        this.hideUnlockTimeout = null;
        this.showUnlockTimeout = null;
        this.isHomePage = document.body.classList.contains('template-index');
        this.isProductPage = document.body.classList.contains('template-product');
        this.isLoginPage = document.body.classList.contains('template-customers-login');
        this.isCartPage = document.body.classList.contains('template-cart');
        // About page: support both the dedicated page.about template and any page rendering the About blocks section.
        this.isAboutPage =
            document.body.classList.contains('template-page-about') ||
            (document.body.classList.contains('template-page') && !!document.querySelector('.apt-blocks'));

        // On product pages, find the breadcrumb so it can follow the header hide/show
        this.productBreadcrumb = null;
        if (this.isProductPage) {
            this.productBreadcrumb = document.querySelector('.product-title-bar .collection-hero__title-wrapper');
        }

        this.init();
    }

    init() {
        // Find the first hero/banner section (typically slideshow, image-banner, etc.)
        this.findHeroBanner();
        this.updateHeaderMetrics();

        // Set up scroll listener
        this.handleScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.handleScroll, { passive: true });

        // Handle window resize
        window.addEventListener('resize', () => {
            if (window.innerWidth >= 990) {
                this.reset();
            } else {
                this.findHeroBanner();
                this.updateHeaderMetrics();
            }
        });

        // Reveal header when an item is added to cart so the user sees the cart update
        this.handleCartItemAdded = this.revealHeaderForCart.bind(this);
        document.addEventListener('cart:item-added', this.handleCartItemAdded);

        // Initial check
        this.checkBackgroundState();
    }

    revealHeaderForCart() {
        if (!this.isHeaderHidden) return;

        // Clear any ongoing animation locks so we can force the header visible
        this.clearAnimationLocks();
        this.showHeader();

        // Reset maxScrollUpPosition so the header doesn't immediately hide again on the next scroll tick
        this.maxScrollUpPosition = window.pageYOffset || document.documentElement.scrollTop;
    }

    findHeroBanner() {
        this.heroBanner = null;
        if (this.heroHeightTimeout) {
            clearTimeout(this.heroHeightTimeout);
            this.heroHeightTimeout = null;
        }

        // Product page: look for product image/gallery
        if (this.isProductPage) {
            const productImageSelectors = [
                '.mobile-image-slider',
                '.custom-image-gallery',
                '.product__media-wrapper',
                '.product__media-list',
                '.product-media-container',
                '.product__media',
                '.product-gallery',
                'product-gallery'
            ];

            for (let selector of productImageSelectors) {
                const element = document.querySelector(selector);
                if (element && element.offsetHeight > 100) {
                    this.heroBanner = element;
                    const initialHeight = Math.max(element.offsetHeight || 0, element.getBoundingClientRect().height || 0);
                    this.heroBannerHeight = initialHeight > 0 ? initialHeight : window.innerHeight;
                    this.updateHeroBannerHeight();
                    this.attachHeroMediaListeners(element);
                    this.deferHeroHeightUpdate();
                    return;
                }
            }
        }

        // About page: prioritize the intro video section as the hero
        if (this.isAboutPage) {
            const aboutSelectors = [
                '.home-video-section',
                'home-video',
                '.home-video__container',
                // Fallback: About blocks hero (first section on the About page)
                '.apt-blocks',
                '.apt-block'
            ];

            for (let selector of aboutSelectors) {
                const element = document.querySelector(selector);
                if (element) {
                    this.heroBanner = element;
                    const initialHeight = Math.max(element.offsetHeight || 0, element.getBoundingClientRect().height || 0);
                    this.heroBannerHeight = initialHeight > 0 ? initialHeight : window.innerHeight;
                    this.updateHeroBannerHeight();
                    this.attachHeroMediaListeners(element);
                    this.deferHeroHeightUpdate();
                    return;
                }
            }
        }

        // Login page or Homepage: look for common hero section selectors
        const selectors = [
            '.home-video-section',
            'home-video',
            '.home-video__container',
            '.slideshow',
            '.banner',
            '.hero',
            '[class*="hero"]',
            '.image-banner',
            'section:first-of-type .slideshow',
            'section:first-of-type .banner'
        ];

        for (let selector of selectors) {
            const element = document.querySelector(selector);
            if (element && element.offsetHeight > 200) { // Ensure it's a significant banner
                this.heroBanner = element;
                const initialHeight = Math.max(element.offsetHeight || 0, element.getBoundingClientRect().height || 0);
                this.heroBannerHeight = initialHeight > 0 ? initialHeight : window.innerHeight;
                this.updateHeroBannerHeight();
                this.attachHeroMediaListeners(element);
                this.deferHeroHeightUpdate();
                break;
            }
        }

        // If no hero found, use a default height
        if (!this.heroBanner) {
            this.heroBannerHeight = window.innerHeight * 0.6; // 60vh as fallback
        }
    }

    updateHeroBannerHeight() {
        if (this.heroBanner) {
            const rect = this.heroBanner.getBoundingClientRect();
            const measuredHeight = Math.max(this.heroBanner.offsetHeight || 0, rect.height || 0);

            if (measuredHeight > 0) {
                this.heroBannerHeight = measuredHeight;
            }
        }
    }

    attachHeroMediaListeners(element) {
        if (!element) return;

        const mediaElements = element.querySelectorAll('video, iframe');
        mediaElements.forEach((media) => {
            if (media.dataset.headerScrollHeroListenerAttached === 'true') return;

            const updateHeight = () => this.updateHeroBannerHeight();
            media.addEventListener('loadedmetadata', updateHeight);
            media.addEventListener('loadeddata', updateHeight);
            media.addEventListener('load', updateHeight);

            media.dataset.headerScrollHeroListenerAttached = 'true';
        });
    }

    deferHeroHeightUpdate() {
        if (this.heroHeightTimeout) {
            clearTimeout(this.heroHeightTimeout);
        }

        this.heroHeightTimeout = setTimeout(() => {
            this.updateHeroBannerHeight();
        }, 300);
    }

    onScroll() {
        // Don't hide header while the menu drawer is open
        if (this.header.classList.contains('menu-open')) return;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollDelta = scrollTop - this.lastScrollTop;
        const isWithinInitialLock = scrollTop <= this.initialHideLockDistance;

        if (Math.abs(scrollDelta) <= this.scrollThreshold) {
            this.lastScrollTop = scrollTop;
            return;
        }

        // Check if we should add background
        this.checkBackgroundState();

        // Scrolling down - move header up with the scroll (no transition)
        if (scrollDelta > 0 && scrollTop > 0) {
            if (isWithinInitialLock) {
                this.maxScrollUpPosition = scrollTop;

                if (this.isHeaderHidden) {
                    this.showHeader();
                }
            } else {
                this.maxScrollUpPosition = Math.max(this.maxScrollUpPosition, scrollTop);

                if (!this.isHeaderHidden) {
                    this.hideHeader();
                }
            }
        }
        // Scrolling up - only show header if scrolled up beyond threshold
        else if (scrollDelta < 0) {
            const scrolledUpAmount = this.maxScrollUpPosition - scrollTop;

            // Only show header if user has scrolled up by the threshold amount
            if (scrolledUpAmount >= this.scrollUpThreshold && this.isHeaderHidden) {
                this.showHeader();
                this.maxScrollUpPosition = scrollTop;
            }
        }

        this.lastScrollTop = scrollTop;
    }

    updateHeaderPosition() {
        this.header.style.transform = `translateY(${this.headerTranslateY}px)`;
        this.updateBreadcrumbPosition();
    }

    updateBreadcrumbPosition() {
        if (!this.productBreadcrumb) return;
        if (this.isHeaderHidden) {
            this.productBreadcrumb.classList.add('breadcrumb--header-hidden');
        } else {
            this.productBreadcrumb.classList.remove('breadcrumb--header-hidden');
        }
    }

    updateHeaderMetrics() {
        const measuredHeight = this.header.offsetHeight || this.headerHeight || 65;
        this.headerHeight = measuredHeight;
        this.initialHideLockDistance = this.headerHeight + this.initialHideLockBuffer;
    }

    hideHeader() {
        if (!this.canHideHeader()) {
            return;
        }

        this.header.classList.add('header--animating');
        this.lockShowUntilAnimationCompletes();
        this.headerTranslateY = -this.headerHeight;
        this.updateHeaderPosition();
        this.isHeaderHidden = true;
    }

    showHeader() {
        if (!this.canShowHeader()) {
            return;
        }

        this.header.classList.add('header--animating');
        this.lockHideUntilAnimationCompletes();
        this.headerTranslateY = 0;
        this.updateHeaderPosition();
        this.isHeaderHidden = false;
    }

    lockHideUntilAnimationCompletes() {
        this.isShowAnimationInProgress = true;

        const totalLockDuration = this.showAnimationDuration + this.hideAfterShowDelay;

        if (this.hideUnlockTimeout) {
            clearTimeout(this.hideUnlockTimeout);
        }
        this.hideLockUntil = Date.now() + totalLockDuration;
        this.hideUnlockTimeout = setTimeout(() => {
            this.hideLockUntil = 0;
            this.hideUnlockTimeout = null;
        }, totalLockDuration);

        if (this.showAnimationTimeout) {
            clearTimeout(this.showAnimationTimeout);
        }
        this.showAnimationTimeout = setTimeout(() => {
            this.isShowAnimationInProgress = false;
            this.showAnimationTimeout = null;
        }, this.showAnimationDuration);
    }

    lockShowUntilAnimationCompletes() {
        this.isHideAnimationInProgress = true;

        if (this.showUnlockTimeout) {
            clearTimeout(this.showUnlockTimeout);
        }
        this.showLockUntil = Date.now() + this.hideAnimationDuration;
        this.showUnlockTimeout = setTimeout(() => {
            this.showLockUntil = 0;
            this.showUnlockTimeout = null;
        }, this.hideAnimationDuration);

        if (this.hideAnimationTimeout) {
            clearTimeout(this.hideAnimationTimeout);
        }
        this.hideAnimationTimeout = setTimeout(() => {
            this.isHideAnimationInProgress = false;
            this.hideAnimationTimeout = null;
        }, this.hideAnimationDuration);
    }

    canHideHeader() {
        if (this.isShowAnimationInProgress) {
            return false;
        }

        if (this.hideLockUntil && Date.now() < this.hideLockUntil) {
            return false;
        }

        return true;
    }

    canShowHeader() {
        if (this.isHideAnimationInProgress) {
            return false;
        }

        if (this.showLockUntil && Date.now() < this.showLockUntil) {
            return false;
        }

        return true;
    }

    clearAnimationLocks() {
        if (this.showAnimationTimeout) {
            clearTimeout(this.showAnimationTimeout);
            this.showAnimationTimeout = null;
        }

        if (this.hideAnimationTimeout) {
            clearTimeout(this.hideAnimationTimeout);
            this.hideAnimationTimeout = null;
        }

        if (this.hideUnlockTimeout) {
            clearTimeout(this.hideUnlockTimeout);
            this.hideUnlockTimeout = null;
        }

        if (this.showUnlockTimeout) {
            clearTimeout(this.showUnlockTimeout);
            this.showUnlockTimeout = null;
        }

        this.hideLockUntil = 0;
        this.showLockUntil = 0;
        this.isShowAnimationInProgress = false;
        this.isHideAnimationInProgress = false;
    }

    checkBackgroundState() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (this.isHomePage || this.isProductPage || this.isLoginPage || this.isCartPage || this.isAboutPage) {
            // Homepage, Product page, Login page & Cart page: Check if we're above the hero/product image section
            // Transparent background when above, white background when past
            if (this.heroBanner && scrollTop <= this.heroBannerHeight) {
                this.headerWrapper.classList.remove('header--has-background');
                this.header.classList.remove('header--past-hero');
            } else {
                this.headerWrapper.classList.add('header--has-background');
                this.header.classList.add('header--past-hero');
            }
        } else {
            // All other pages: Always show white background
            this.headerWrapper.classList.add('header--has-background');
            this.header.classList.add('header--past-hero');
        }
    }

    reset() {
        // Reset styles
        this.header.style.transform = 'translateY(0)';
        this.headerTranslateY = 0;
        this.isHeaderHidden = false;
        this.header.classList.remove('header--past-hero');
        this.headerWrapper.classList.remove('header--has-background');

        if (this.productBreadcrumb) {
            this.productBreadcrumb.style.transform = '';
            this.productBreadcrumb.style.transition = '';
        }

        // Clear timeout
        if (this.heroHeightTimeout) {
            clearTimeout(this.heroHeightTimeout);
            this.heroHeightTimeout = null;
        }

        this.clearAnimationLocks();

        // Remove scroll listener when on desktop
        if (this.handleScroll) {
            window.removeEventListener('scroll', this.handleScroll);
        }
    }

    destroy() {
        // Remove all event listeners
        if (this.handleScroll) {
            window.removeEventListener('scroll', this.handleScroll);
        }

        if (this.handleCartItemAdded) {
            document.removeEventListener('cart:item-added', this.handleCartItemAdded);
        }

        // Reset styles
        if (this.header) {
            this.header.style.transform = '';
            this.headerTranslateY = 0;
        }

        if (this.productBreadcrumb) {
            this.productBreadcrumb.classList.remove('breadcrumb--header-hidden');
        }

        // Clear timeout
        if (this.heroHeightTimeout) {
            clearTimeout(this.heroHeightTimeout);
            this.heroHeightTimeout = null;
        }

        this.clearAnimationLocks();
    }
}

// Handle responsive initialization
(function () {
    let headerScrollInstance = null;
    const MOBILE_BREAKPOINT = 989;

    function isStudioPage() {
        return document.body && document.body.classList.contains('template-page-studio');
    }

    function isMobileViewport() {
        return window.innerWidth <= MOBILE_BREAKPOINT;
    }

    function initHeaderScroll() {
        // Studio page: keep the mobile header always visible and transparent.
        // (Disable this scroll-driven hide/show behavior only on that template.)
        if (isStudioPage()) return;

        if (isMobileViewport() && !headerScrollInstance) {
            headerScrollInstance = new MobileHeaderScroll();
        }
    }

    function destroyHeaderScroll() {
        if (!isMobileViewport() && headerScrollInstance) {
            if (typeof headerScrollInstance.destroy === 'function') {
                headerScrollInstance.destroy();
            }
            headerScrollInstance = null;
        }
    }

    function handleResize() {
        if (isMobileViewport()) {
            initHeaderScroll();
        } else {
            destroyHeaderScroll();
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initHeaderScroll();
            window.addEventListener('resize', handleResize, { passive: true });
        });
    } else {
        initHeaderScroll();
        window.addEventListener('resize', handleResize, { passive: true });
    }
})();
